import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { noop } from '../lib/serviceHelpers';

// ==================== SUPPORT FORMS ====================

export const useFetchSupportForms = (params = {}, opts = {}) => {
  return useQuery(
    ['supportForms', params],
    async () => {
      const res = await axios.get(`/admin/support-forms`, { params });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      ...opts,
    },
  );
};

export const useFetchSupportForm = (formId, opts = {}) => {
  return useQuery(
    ['supportForms', formId],
    async () => {
      const res = await axios.get(`/admin/support-forms/${formId}`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      enabled: !!formId,
      ...opts,
    },
  );
};

export const useCreateSupportForm = (callback, opts = {}) => {
  return useMutation(
    async (payload) => {
      const res = await axios.post(`/admin/support-forms`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdateSupportForm = (callback, opts = {}) => {
  return useMutation(
    async ({ formId, payload }) => {
      const res = await axios.put(`/admin/support-forms/${formId}`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDeleteSupportForm = (callback, opts = {}) => {
  return useMutation(
    async (formId) => {
      const res = await axios.delete(`/admin/support-forms/${formId}`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// ==================== FORM FIELDS ====================

export const useFetchFormFields = (formId, opts = {}) => {
  return useQuery(
    ['supportFormFields', formId],
    async () => {
      const res = await axios.get(`/admin/support-forms/${formId}/fields`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      enabled: !!formId,
      ...opts,
    },
  );
};

export const useCreateFormField = (callback, opts = {}) => {
  return useMutation(
    async ({ formId, payload }) => {
      const res = await axios.post(`/admin/support-forms/${formId}/fields`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdateFormField = (callback, opts = {}) => {
  return useMutation(
    async ({ formId, fieldId, payload }) => {
      const res = await axios.put(`/admin/support-forms/${formId}/fields/${fieldId}`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDeleteFormField = (callback, opts = {}) => {
  return useMutation(
    async ({ formId, fieldId }) => {
      const res = await axios.delete(`/admin/support-forms/${formId}/fields/${fieldId}`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useReorderFormFields = (callback, opts = {}) => {
  return useMutation(
    async ({ formId, fieldOrders }) => {
      const res = await axios.post(`/admin/support-forms/${formId}/fields/reorder`, { fieldOrders });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// ==================== BOARD CONFIGS ====================

export const useFetchBoardConfigs = (opts = {}) => {
  return useQuery(
    ['boardConfigs'],
    async () => {
      const res = await axios.get(`/admin/support-forms/board-configs`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      ...opts,
    },
  );
};

export const useFetchBoardConfig = (configId, opts = {}) => {
  return useQuery(
    ['boardConfigs', configId],
    async () => {
      const res = await axios.get(`/admin/support-forms/board-configs/${configId}`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      enabled: !!configId,
      ...opts,
    },
  );
};

export const useCreateBoardConfig = (callback, opts = {}) => {
  return useMutation(
    async (payload) => {
      const res = await axios.post(`/admin/support-forms/board-configs`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdateBoardConfig = (callback, opts = {}) => {
  return useMutation(
    async ({ configId, payload }) => {
      const res = await axios.put(`/admin/support-forms/board-configs/${configId}`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDeleteBoardConfig = (callback, opts = {}) => {
  return useMutation(
    async (configId) => {
      const res = await axios.delete(`/admin/support-forms/board-configs/${configId}`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// ==================== MONDAY.COM ====================

export const useFetchMondayBoards = (opts = {}) => {
  return useQuery(
    ['mondayBoards'],
    async () => {
      const res = await axios.get(`/admin/monday/boards`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60000, // Cache boards for 1 minute
      ...opts,
    },
  );
};

export const useFetchMondayColumns = (boardId, opts = {}) => {
  return useQuery(
    ['mondayColumns', boardId],
    async () => {
      const res = await axios.get(`/admin/monday/boards/${boardId}/columns`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60000, // Cache columns for 1 minute
      enabled: !!boardId,
      ...opts,
    },
  );
};
